import { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import {
  Box,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { LoginWindow } from "../../components/LoginWindow";
import { summarizeUsers } from "../../utils/client";
import { downloadCSV, isAdmin, isoTimestamp } from "../../utils/helper";
import { useUserData } from "../../components/DataProvider";
import TopNavBar from "../../components/TopNavBar";
import { AttendeeSummary, ManagerSummary } from "baptize-america-client";
import DebouncedButton from "../../components/DebounceButton";
import { Download } from "@phosphor-icons/react";

const DisplayUsers = () => {
  const [managers, setManagers] = useState<ManagerSummary[]>([]);
  const [attendees, setAttendees] = useState<AttendeeSummary[]>([]);

  const theme = useTheme();
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();

  const styles = {
    navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "column",
      mb: 5,
    },
  };

  useEffect(() => {
    const getData = async () => {
      const result = await summarizeUsers();
      setAttendees(result.attendees ?? []);
      setManagers(result.managers ?? []);
    };

    getData();
  }, []);

  return authStatus === "authenticated" && isAdmin(attributes) ? (
    <Container>
      <Box
        sx={[
          {
            "& .MuiTextField-root": {
              m: 1,
              width: theme.breakpoints.values.sm,
            },
          },
          styles.navBar,
        ]}
      >
        <Box sx={{ mb: 4 }}>
          <TopNavBar showBackArrow={true} />
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="95vw"
        >
          <Typography m={"1rem"} variant={"h3"}>
            Church Managers
          </Typography>
          <DebouncedButton
            startIcon={<Download weight={"fill"} />}
            onClick={() =>
              downloadCSV(managers, `ChurchManagers-${isoTimestamp()}`)
            }
          >
            Export to CSV
          </DebouncedButton>
        </Stack>
        <TableContainer sx={{ width: "95vw" }} component={Paper}>
          <Table size="small" stickyHeader={true}>
            <TableHead>
              <TableRow>
                <TableCell>Church</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {managers.map((manager) => (
                <TableRow
                  key={manager.sub}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Link href={`/churches/${manager.churchId}`}>
                      {manager.churchName}
                    </Link>
                  </TableCell>
                  <TableCell>{manager.firstName}</TableCell>
                  <TableCell>{manager.lastName}</TableCell>
                  <TableCell>{manager.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="95vw"
        >
          <Typography m={"1rem"} variant={"h3"}>
            Attendees
          </Typography>
          <DebouncedButton
            startIcon={<Download weight={"fill"} />}
            onClick={() =>
              downloadCSV(attendees, `Attendees-${isoTimestamp()}`)
            }
          >
            Export to CSV
          </DebouncedButton>
        </Stack>
        <TableContainer sx={{ width: "95vw" }} component={Paper}>
          <Table size="small" stickyHeader={true}>
            <TableHead>
              <TableRow>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendees.map((attendee) => (
                <TableRow
                  key={`${attendee.sub}-${attendee.baptismId}`}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Link href={`/churches/${attendee.churchId}`}>
                      {attendee.firstName}
                    </Link>
                  </TableCell>
                  <TableCell>{attendee.lastName}</TableCell>
                  <TableCell>{attendee.email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  ) : (
    <LoginWindow />
  );
};

export default DisplayUsers;
